import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ViewTabs } from "../../../../common/components";
import { translate } from "../../../../common/providers";
import styles from "./DSIFormStudio.module.css";
import ViewPanel from "./components/view-panel/ViewPanel";
import CriteriaPanel from "./components/critera-panel/CriteriaPanel";
import FormStudioSidePanels from "./components/side-panels/FormStudioSidePanels";
import ViewCreation from "./components/view-panel/view-creation/ViewCreation";
import { useApi } from "../../../../common/hooks";
import backofficeService from "../../../../api/services/back-office/backoffice-service";
import CommitmentDefinitionPanel from "../dsi-commitment-definition/components/commitment-definition-panel/CommitmentDefinitionPanel";
import OpenCommitmentsPanel from "../dsi-commitment-definition/components/open-commitments-panel/OpenCommitmentsPanel";
import ThemeManagementPanel from "../dsi-commitment-definition/components/theme-management-panel/ThemeManagementPanel";
import { CommitmentDefinitionService } from "../../../../api";

export default function DSIFormStudio({ platformCompanies }) {
	const [selectedView, setSelectedView] = useState(null);
	const [selectedTab, setSelectedTab] = useState(0);
	const [selectedPanel, setSelectedPanel] = useState("");
	const [selectedRow, setSelectedRow] = useState(null);
	const [viewCreationMode, setViewCreationMode] = useState(false);
	const [projectsRequest, setProjectsRequest] = useState(null);
	const [themes, setThemes] = useState([]);
	const [openThemeManagement, setOpenThemeManagement] = useState(false);
	const [themeRefreshCount, setThemeRefreshCount] = useState(0);
	const { call: getAllProjects } = useApi(backofficeService.getAllProjects);
	const { call: getAllThemes } = useApi(CommitmentDefinitionService.getAllThemes);
	const [count, setCount] = useState(0);
	const navigate = useNavigate();
	const { tab, subTab } = useParams();
	const tabs = useMemo(
		() => [
			{ label: translate("dsi.form-studio.tab.views"), param: "custom-view" },
			{ label: translate("dsi.form-studio.tab.criteria"), param: "criteria" },
			{ label: translate("dsi.commitment-definition.tab.predefined"), param: "predefined" },
			{ label: translate("dsi.commitment-definition.tab.open"), param: "open" },
		],
		[]
	);
	useEffect(() => {
		setProjectsRequest(
			() =>
				({ page, limit, search }) =>
					getAllProjects({ page, limit, filter: search })
		);
	}, [count, getAllProjects]);
	useEffect(() => {
		if (subTab) {
			const index = tabs.findIndex((t) => t.param === subTab);
			if (index >= 0) {
				setSelectedTab(index);
			}
		}
	}, [subTab, tabs]);
	const handleChangeSelectedTab = (_, newTab) => {
		const { param } = tabs[newTab];
		setSelectedTab(newTab);
		if (tab && param) {
			navigate(`/dsi/${tab}/${param}`, { replace: true });
		}
	};
	const handleSelectPanel = ({ panel, row }) => {
		setSelectedPanel(panel);
		setSelectedRow(row);
	};
	const handleClosePanel = () => {
		setSelectedPanel("");
		setSelectedRow(null);
	};
	const handleOpenViewCreationMode = (view) => {
		if (!selectedTab) {
			if (viewCreationMode) {
				setSelectedView(null);
			}
			setViewCreationMode((prev) => !prev);
		}
		setSelectedView(view || null);
	};
	const handleUpdateRow = () => {
		setCount((prev) => prev + 1);
	};

	const handleOpenThemeManagement = () => {
		setOpenThemeManagement((prev) => !prev);
	};

	const handleRefreshThemes = useCallback(() => {
		setThemeRefreshCount((prev) => prev + 1);
	}, []);

	useEffect(() => {
		getAllThemes()
			.then((data) => setThemes(data || []))
			.catch(console.error);
	}, [getAllThemes, themeRefreshCount]);

	const renderTabContent = () => {
		switch (selectedTab) {
			case 0:
				return (
					<ViewPanel
						refreshList={count}
						onOpenCreation={handleOpenViewCreationMode}
						onSelectPanel={handleSelectPanel}
					/>
				);
			case 1:
				return <CriteriaPanel onSelectPanel={handleSelectPanel} />;
			case 2:
				return (
					<CommitmentDefinitionPanel
						themes={themes}
						onOpenThemeManagement={handleOpenThemeManagement}
						onRefreshThemes={handleRefreshThemes}
					/>
				);
			case 3:
				return <OpenCommitmentsPanel />;
			default:
				return null;
		}
	};

	return (
		<div className={styles.main}>
			{!viewCreationMode && (
				<>
					<div className={styles.tabs}>
						<ViewTabs
							noPadding
							noShadow
							selectedTab={selectedTab}
							tabClassName={styles.tab}
							tabs={tabs}
							onChange={handleChangeSelectedTab}
						/>
					</div>
					{renderTabContent()}
				</>
			)}
			{selectedTab === 0 && viewCreationMode && (
				<ViewCreation view={selectedView} onGoBack={handleOpenViewCreationMode} />
			)}
			<FormStudioSidePanels
				open={!!selectedPanel}
				panel={selectedPanel}
				platformCompanies={platformCompanies}
				projectsRequest={projectsRequest}
				row={selectedRow}
				onClose={handleClosePanel}
				onUpdateRow={handleUpdateRow}
			/>
			<ThemeManagementPanel
				open={openThemeManagement}
				themes={themes}
				onClose={handleOpenThemeManagement}
				onRefresh={handleRefreshThemes}
			/>
		</div>
	);
}
